// CODE REUSE: This app renders both PaymentPageLayout (regular portals) and SystemSubdomain (system pages).
// SystemSubdomain is like PaymentPageLayout but without payment gateways or transaction processing.
// The /portal/check endpoint determines which type a URL is. CustomPage component handles both.
// Both use 'pageType' field for custom pages. When adding page types, update both backend models.

// Must be first: guarantees `process.env` exists so a stray unreplaced
// `process.env.X` can never white-screen the app. See utils/process-shim.js.
import './utils/process-shim';

import LogRocket from 'logrocket';
window.LogRocket = LogRocket;

import {Provider} from '@preact/prerender-data-provider';
import {Router} from "preact-router";
import { useState, useEffect } from 'preact/compat';
import axios from "./utils/axios-interceptor";
import './styles/main.scss';
import Index from "./routes";
import Payment from "./routes/payment";
import Receipt from "./routes/receipt";
import Talk from "./routes/talk";
import OAuthSuccess from "./routes/oauth-success";
import OAuthError from "./routes/oauth-error";
import LegalIndex from "./routes/legal";
import CustomPage from "./routes/legal/custom-page";
import Invoice from "./routes/invoice";
import InvoiceStatus from "./routes/invoice-status";
import History from "./routes/history";
import Login from "./routes/login";
import Account from "./routes/account";
import AdminRedirect from "./routes/admin-redirect";
import CardEnrollment from "./routes/card-enrollment";
import CheckoutSession from "./routes/checkout-session";
import PromiseToPay from "./routes/promise-to-pay";
import ManagePaymentMethods from "./routes/manage-payment-methods";
import AddOnlyPaymentMethods from "./routes/manage-payment-methods-add-only";
import Pos from "./routes/pos";

import {captureError} from "./utils/sentry";
import {perfLogger} from "./utils/perf-logger";
import PageLoader from "./components/ui/page-loader/page-loader";

const VersionPage = () => (
    <div style="padding:40px;font-family:monospace;">
        <h2>bro-builder</h2>
        <p>Version: {process.env.APP_VERSION}</p>
        <p>Built: {new Date().toISOString()}</p>
    </div>
);

console.log(`[bro-builder] v${process.env.APP_VERSION}`);

if (window.location.hostname === "localhost" && process.env.NODE_ENV === 'development') {
    require("preact/debug");
}

// Detect stale chunks after a deploy: if the browser requests a JS chunk
// that no longer exists, the server returns index.html (HTML), which causes
// a SyntaxError when parsed as JS. Force one reload to get fresh assets.
const RELOAD_KEY = '__bro_chunk_reload';
const handleStaleChunk = (error) => {
    if (!error) return false;
    const msg = (error.message || error.reason || '') + '';
    const isChunkError = msg.includes("Unexpected token '<'")
        || msg.includes('Loading chunk')
        || msg.includes('ChunkLoadError')
        || (error && error.name === 'ChunkLoadError');
    if (isChunkError && !sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        // Show a brief overlay so the user knows what's happening
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.92);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;';
        overlay.innerHTML = '<div style="text-align:center"><div style="width:36px;height:36px;border-radius:50%;border:3px solid rgba(0,0,0,0.08);border-top-color:#888;animation:spin .8s ease-in-out infinite;margin:0 auto 16px"></div><div style="font-size:15px;color:#444">Updating to latest version\u2026</div></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style>';
        document.body.appendChild(overlay);
        setTimeout(() => window.location.reload(), 300);
        return true;
    }
    return false;
};

window.addEventListener('error', (event) => {
    if (handleStaleChunk(event.error || { message: event.message })) return;
    captureError(event.error || event.message, { type: 'global-error' });
});

window.addEventListener('unhandledrejection', (event) => {
    if (handleStaleChunk(event.reason)) return;
    const reason = event.reason;
    // Ignore plain string rejections (e.g. "Timeout" from NMI CollectJS or antifingerprint
    // library) — these are not actionable and generate noise in Sentry (PORTALSBACK-3H).
    if (typeof reason === 'string') return;
    // Ignore NMI CollectJS internal crash: closePaymentRequest() called when this.iframe is
    // undefined due to PaymentRequestAbstraction failure — crash is inside NMI's own SDK,
    // not fixable in our code (PORTALSBACK-6W, PORTALSBACK-6D).
    if (reason?.message?.includes("Cannot read properties of undefined (reading 'classList')")
        || reason?.message?.includes("undefined is not an object (evaluating 'this.iframe.classList')")) return;
    // Ignore CollectJS MessageHandler null-ref crashes — SDK tries to access .style or
    // .dispatchEvent on iframes removed from DOM after navigation/unmount. (GW/G2/GT)
    // Chrome: "Cannot read properties of null (reading 'style')" / "Cannot read properties of null (reading 'dispatchEvent')"
    // Safari: "null is not an object (evaluating '....style')" / "null is not an object (evaluating '....dispatchEvent')"
    if (reason?.message) {
        const m = reason.message;
        const isNullPropChrome = m.includes('Cannot read properties of null')
            && (m.includes("reading 'style'") || m.includes("reading 'dispatchEvent'"));
        const isNullPropSafari = m.includes('null is not an object')
            && (m.includes('.style') || m.includes('.dispatchEvent'));
        if (isNullPropChrome || isNullPropSafari) return;
    }
    // Ignore CollectJS internal "Object Not Found Matching Id" crash — SDK's own
    // iframe update fails after DOM changes, not fixable in our code. (PORTALSBACK-4M)
    if (reason?.message?.includes('Object Not Found Matching Id') && reason?.message?.includes('MethodName:update')) return;
    // Ignore plain object rejections from payment gateway SDK callbacks —
    // these are {errorType, message} objects, not real Error instances (PORTALSBACK-HV)
    if (reason && typeof reason === 'object' && !(reason instanceof Error) && 'errorType' in reason) return;
    // Convert non-Error rejections to proper Errors so Sentry gets a readable message
    const err = reason instanceof Error ? reason : new Error(typeof reason === 'object' ? JSON.stringify(reason) : String(reason));
    captureError(err, { type: 'unhandled-rejection' });
});

// Clear stale chunk reload flag on successful load
sessionStorage.removeItem(RELOAD_KEY);

// Unregister service workers to clear stale caches
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
    });
    caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
    });
}

/**
 * Wrapper component to extract slug from URL path for CustomPage
 * preact-router passes the captured route params as props
 */
const CustomPageWrapper = ({ slug, isSystemSubdomain, ...rest }) => {
    return <CustomPage slug={slug || ''} isSystemSubdomain={isSystemSubdomain} {...rest} />;
};

const App = (props) => {
    const [portalInfo, setPortalInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initPortal = async () => {
            const perf = perfLogger.start('app-init');
            try {
                const hostname = window.location.hostname;

                // Get the API URL from dns-resolver
                let dnsResolverUrl = '';
                if (hostname === 'localhost' && localStorage.getItem('useLocalhostApi') === 'true') {
                    dnsResolverUrl = 'http://localhost:5001/api/dns-resolver/resolve';
                } else {
                    const resolverBase = process.env.PREACT_APP_DNSRESOLVER_API || 'https://dns.ourcheckout.net';
                    dnsResolverUrl = `${resolverBase}/api/dns-resolver/resolve`;
                }

                perf.mark('app.dns-resolve');
                const dnsResponse = await axios.get(dnsResolverUrl);
                perf.measure('app.dns-resolve');

                let apiUrl = dnsResponse.data.api;

                if (hostname === 'localhost' || hostname.includes('portalsfront.ngrok.app')) {
                    apiUrl = `${process.env.PREACT_APP_LOCALHOST_API}api/`;
                }

                // Cache DNS result globally for route reuse
                window.__dnsData = { ...dnsResponse.data, api: apiUrl };

                // Single /portal/init call replaces portal/check + get-info + get-logger
                perf.mark('app.portal-init');
                const lookupHostname = (hostname === 'localhost' && process.env.PREACT_APP_LOCALHOST_CURRENT_HOSTNAME)
                    ? process.env.PREACT_APP_LOCALHOST_CURRENT_HOSTNAME
                    : hostname;
                const initParams = { url: lookupHostname };
                // Forward Painter Bros query params if present
                const urlParams = new URLSearchParams(window.location.search);
                if (urlParams.get('JobId')) initParams.JobId = urlParams.get('JobId');
                if (urlParams.get('franchise')) initParams.franchise = urlParams.get('franchise');
                if (urlParams.get('EstimationId')) initParams.EstimationId = urlParams.get('EstimationId');

                const response = await axios.get(`${apiUrl}portal/init`, { params: initParams });
                perf.measure('app.portal-init');

                // Cache the full init response for the route to consume
                window.__portalData = response.data;

                perf.addMetadata({ apiUrl, isSystemSubdomain: response.data.isSystemSubdomain });
                perf.send(apiUrl);

                setPortalInfo(response.data);
            } catch (error) {
                console.error('Error initializing portal:', error);
                captureError(error, { type: 'portal-init-error' });
                // Default to regular portal on error
                setPortalInfo({ found: false, isSystemSubdomain: false });
            } finally {
                setLoading(false);
            }
        };

        initPortal();
    }, []);

    // Show loading state while determining portal type
    if (loading) {
        return <PageLoader />;
    }

    // If this is a SystemSubdomain, show custom pages from database
    if (portalInfo && portalInfo.isSystemSubdomain) {
        return (
            <Provider value={props}>
                <Router>
                    <LegalIndex path="/"/>
                    <CustomPageWrapper path="/:slug*" isSystemSubdomain={true} />
                </Router>
            </Provider>
        );
    }

    // Default routes for regular payment portals
    return (
        <Provider value={props}>
            <Router>
                <Index path="/"/>
                <Talk path="/talk"/>
                <Receipt path="/receipt"/>
                <Payment path="/payment"/>
                <Invoice path="/invoice/:token"/>
                <InvoiceStatus path="/invoice-status/:token"/>
                <CardEnrollment path="/enroll/:token"/>
                <CheckoutSession path="/checkout-session/:token"/>
                <PromiseToPay path="/promise-to-pay/:token"/>
                {/* Add-only (write-only) card page — spec 13. Declared BEFORE the
                    full manage route; distinct segment count so neither shadows
                    the other under preact-router. */}
                <AddOnlyPaymentMethods path="/customer/external/:externalCustomerId/payment-methods/add-only"/>
                <ManagePaymentMethods path="/customer/external/:externalCustomerId/payment-methods"/>
                <History path="/history"/>
                <Login path="/login"/>
                <Account path="/account"/>
                <AdminRedirect path="/admin"/>
                <OAuthSuccess path="/oauth/success"/>
                <OAuthError path="/oauth/error"/>
                <Pos path="/pos"/>
                <VersionPage path="/version"/>
            </Router>
        </Provider>
    )
}

export default App;
